import React from "react";
import Term from "../term";

const UserDataProtection = () => {
  return (
    <section id="user-data-protection">
      <h2>User Data Protection</h2>
      <div className="term-condition-v2__content-text">
        <p>
          Welcome to the EMOS User Data Protection Policy. EMOS aims to provide
          clarity and assurance to Users regarding how EMOS collects, uses, and
          protects their personal information. By reading this User Data
          Protection Policy, Users are expected to feel confident and assured
          that their privacy is our top priority.
        </p>
        <p>
          In this User Data Protection Policy, we affirm that PT EMOS Global
          Digital (referred to as "EGD") acts as the Data Controller and is
          committed to safeguarding Users' data to facilitate secure
          transactions.
        </p>
        <p>
          EGD prioritizes the protection of Users' Personal Data above all else.
          This User Data Protection Policy outlines in detail the definitions,
          categories, legality, and purposes associated with the processing of
          Users' Personal Data. Furthermore, EGD clarifies the management and
          transfer of Personal Data, the duration of its processing, and the
          procedures for updating this policy. All actions are conducted in
          compliance with Law No. 27 of 2022 on Personal Data Protection and its
          amendments, commonly known as the "PDP Law", along with other relevant
          regulations, ensuring that Users can confidently share their Personal
          Data with us.
        </p>
        <p>
          To provide clarity, the categories, grounds for processing, and
          objectives of processing Users' Personal Data can differ based on the
          specific services utilized by the User.
        </p>
      </div>
      <Term title="1. Definition of Personal Data" id="data-1">
        <p>
          Personal Data refers to information concerning an individual that can
          be identified, either directly or indirectly, either alone or in
          conjunction with other data, regardless of whether it is processed
          through electronic or non-electronic means.
        </p>
      </Term>

      <Term title="2. Types of Personal Data" id="data-2">
        <p>
          EGD recognizes the significance of informing Users about the various
          categories and types of their Personal Data that may be processed. The
          Personal Data handled by EGD can be obtained directly from the User or
          through the User's Supplier, which in this context refers to an EGD
          partner Distributor. The following categories of data are included:{" "}
        </p>
        <ol className="list-style-alpha">
          <li>
            Identification data for personal profiles, which encompasses full
            name, National Identification Number (NIK) for Indonesian Citizens
            (WNI) and Foreign Nationals (WNA), Taxpayer Identification Number
            (NPWP), gender, nationality, place and date of birth, signature
            (both wet and/or electronic), and/or biometric information.
          </li>
          <li>
            Correspondence information, including the address as listed on the
            ID card, email address, and phone/mobile number.
          </li>
          <li>
            Employment-related data, such as job type, industry, position, name
            of the company or organization, and workplace address.{" "}
          </li>
          <li>Financial information, which includes transaction details. </li>
          <li>
            Digital activity information, such as geolocation, IP address, and
            User interactions on EGD.{" "}
          </li>
          <li>
            Data on personal preferences, including communication preferences.{" "}
          </li>
        </ol>
      </Term>

      <Term title="3. Legality of Personal Data Processing" id="data-3">
        <ol className="list-style-alpha">
          <li>
            <p>Basis for Processing</p>
            <p>
              The processing of Personal Data by EGD is conducted when one or
              more of the following conditions are met:{" "}
            </p>
            <ol>
              <li>EGD has obtained the User's explicit and lawful consent.</li>
              <li>
                EGD is fulfilling its rights and responsibilities as outlined in
                an agreement with the User.{" "}
              </li>
              <li>
                EGD is required to exercise its authority or meet obligations in
                accordance with relevant laws, regulations, or directives from
                authorized entities.{" "}
              </li>
              <li>EGD must act to protect the vital interests of the User.</li>
              <li>
                EGD is engaged in activities that serve the public interest
                and/or public service.{" "}
              </li>
              <li>
                EGD is pursuing other legitimate interests, ensuring a balance
                between its own interests and the rights of the User.{" "}
              </li>
            </ol>
          </li>
          <li>
            <p>Purpose of Processing </p>
            <p>EGD processes Users' Personal Data for several key purposes: </p>
            <ol>
              <li>
                Managing services, which includes profiling and scoring, aimed
                at enhancing User experiences and managing EGD's associated
                risks.
              </li>
              <li>
                Delivering promotions or programs from EGD, potentially in
                partnership with third parties, related to products and/or
                services that the User currently possesses.
              </li>
              <li>
                Marketing and offering EGD's services, as well as those from
                other companies within the Kalbe Group and/or third parties
                collaborating with EGD, for products and/or services that the
                User has yet to acquire.
              </li>
              <li>
                Ensuring compliance with relevant laws and regulations, along
                with adhering to directives from regulatory bodies, law
                enforcement, and other authorized authorities.
              </li>
            </ol>
          </li>
        </ol>
      </Term>

      <Term title="4. Data Management and Transfer" id="data-4">
        <p>
          In processing Users' Personal Data, EGD may involve the User's
          Suppliers, third parties as joint data controllers, and/or processors
          of Users' Personal Data. In such cases, EGD will ensure the protection
          of Users' Personal Data in accordance with applicable laws and
          regulations.{" "}
        </p>
      </Term>

      <Term title="5. Users' Rights Regarding Personal Data" id="data-5">
        <p>
          EGD acknowledges that Personal Data is a vital asset for Users.
          Consequently, we aim to inform Users of their rights as subjects of
          Personal Data, outlined as follows:
        </p>
        <ol className="list-style-alpha">
          <li>
            <p>Right to Information and Access </p>
            <p>
              Users are entitled to receive information about the identity of
              the entity requesting their Personal Data, the purpose behind the
              request, and to access copies of their Personal Data. EGD will
              facilitate access to this information through official channels,
              including Suppliers and Health Supply License Holders
              (Principals), in compliance with relevant laws and EGD policies.
              Users should be aware that requesting a copy of their Personal
              Data and/or information regarding its processing may result in a
              fee charged by EGD.
            </p>
          </li>
          <li>
            <p>Right to Correct Data </p>
            <p>
              Users have the right to complete, update, or rectify any
              inaccurate or incomplete Personal Data.{" "}
            </p>
          </li>
          <li>
            <p>
              Right to Obtain, Use, and/or Transfer Personal Data to Other
              Parties
            </p>
            <p>
              Users have the right to obtain, utilize, or transfer their
              Personal Data held by EGD to third parties, provided that the
              communication systems employed by both EGD and the third party are
              secure.
            </p>
          </li>
          <li>
            <p>
              Right to Terminate Processing, Delete, and/or Destroy Personal
              Data
            </p>
            <p>
              Users possess the right to halt the processing, delete, and/or
              eliminate their Personal Data. They are expected to allow EGD
              adequate time to carry out the termination of processing,
              deletion, and/or destruction of their Personal Data, as stipulated
              by EGD. To invoke this right, Users may reach out to EGD via the
              communication methods specified in this User Data Protection
              Policy.
            </p>
            <p>
              It is crucial for Users to recognize that halting processing,
              deleting, and/or destroying Personal Data could hinder EGD's
              ability to deliver services and may affect existing contractual
              agreements with third parties. Consequently, this may lead to the
              cessation of services provided to the User and/or the termination
              of one or more contracts between the User and EGD, as well as
              potential breaches of the User's obligations under agreements with
              EGD.
            </p>
            <p>
              Furthermore, the termination of processing, deletion, and/or
              destruction of Personal Data may result in the suspension of the
              User's account. Any consequences arising from the exercise of the
              User's right to terminate processing, delete, and/or destroy
              Personal Data will be the User's responsibility.
            </p>
          </li>
          <li>
            <p>
              EGD is not required to delete or eliminate Personal Data for the
              following reasons:{" "}
            </p>
            <ol>
              <li>National defense and security considerations</li>
              <li>Law enforcement activities</li>
              <li>Public interest related to state administration</li>
              <li>
                Oversight of the healthcare sector within state administration
              </li>
            </ol>
          </li>
          <li>
            <p>Right to Revoke Consent</p>
            <p>
              Users retain the right to revoke their consent for the processing
              of Personal Data previously provided to EGD. Users acknowledge
              that EGD may require additional time to finalize the cessation of
              Personal Data processing. To exercise this right, Users can reach
              out to EGD via the communication methods specified in this User
              Data Protection Policy.
            </p>
            <p>
              It is crucial for Users to recognize that revoking consent may
              impact EGD's ability to deliver services and manage existing
              contractual relationships with third parties. This could lead to
              the suspension of services provided to the User and/or the
              termination of one or more agreements between the User and EGD, as
              well as potential breaches of User obligations under agreements
              with EGD.
            </p>
            <p>
              In this regard, the withdrawal of consent for Personal Data
              processing grants EGD the right to block the User's account. Any
              losses arising from the exercise of the User's right to withdraw
              consent for the processing of Personal Data will be the
              responsibility of the User.{" "}
            </p>
          </li>
          <li>
            <p>Right to Object to Automated Processing Results</p>
            <p>
              Users possess the right to contest the outcomes of automated
              processing of their Personal Data, particularly when such
              processing leads to legal implications or has a substantial impact
              on the User, including activities like profiling and credit
              scoring.
            </p>
          </li>
          <li>
            <p>Right to Suspend or Limit Processing</p>
            <p>
              Users possess the right to suspend or limit the processing of
              their Personal Data in accordance with the intended purpose of
              such processing. Users acknowledge that they will provide EGD with
              sufficient time to address the suspension or limitation of
              Personal Data processing as required. To initiate the process of
              suspending or limiting processing, Users may reach out to EGD via
              the communication methods specified in this User Data Protection
              Policy.
            </p>
            <p>
              It is crucial for Users to recognize that a request to suspend or
              limit processing may hinder EGD's ability to deliver services to
              Users and could affect existing contractual relationships with
              other third parties. This may lead to the cessation of services
              provided to the User and/or the termination of one or more
              agreements between the User and EGD, as well as potential breaches
              of User obligations under agreements with EGD.
            </p>
            <p>
              In this regard, the suspension or limitation of Personal Data
              processing may result in EGD exercising the right to block the
              User's account. Any consequences arising from the User's decision
              to restrict Personal Data processing will be the User's
              responsibility.
            </p>
          </li>
        </ol>
      </Term>

      <Term title="6. User Consent for Personal Data Processing" id="data-6">
        <p>
          I consent to EGD processing my Personal Data in accordance with the
          Personal Data Protection Law (UU PDP) and its amendments, along with
          other applicable regulations and the User Data Protection Policy, for
          the following purposes:
        </p>
        <ol>
          <li>
            Service Management (including profiling and scoring) aimed at
            enhancing services for both Users and EGD.
          </li>
          <li>
            Offering promotions or programs provided by EMOS, in partnership
            with other entities, related to services that the User currently
            possesses.
          </li>
          <li>
            Adhering to legal and regulatory requirements as well as directives
            from regulatory bodies, law enforcement, and other relevant
            institutions.
          </li>
          <li>
            Marketing and/or promoting services from collaborating companies
            and/or third parties associated with EGD, concerning services that
            the User has not yet acquired.
          </li>
        </ol>
        <p>By providing this consent, the User acknowledges and agrees that:</p>
        <ol>
          <li>
            The User and any parties whose Personal Data is shared with EGD have
            granted permission and authorization for EGD to process the Personal
            Data as necessary for the aforementioned purposes.
          </li>
          <li>
            All Personal Data submitted to EGD is accurate and up-to-date, and
            the User commits to notifying or updating their information through
            the EGD application or by contacting EMOS Care to ensure that their
            Personal Data reflects the most current and valid information.
          </li>
          <li>
            The User accepts full responsibility for any tangible or intangible
            losses resulting from a failure to safeguard Personal Data due to
            negligence or intentional actions by the User or any authorized
            individuals.
          </li>
          <li>
            EGD may modify the User's Personal Data as mandated by law, upon
            request from regulatory bodies or competent authorities, or to
            safeguard the interests of the User and/or EGD.
          </li>
        </ol>
      </Term>

      <Term title="7. Revisions to the User Data Protection Policy" id="data-7">
        <p>
          EGD is dedicated to safeguarding the security and privacy of User
          information. As such, this User Data Protection Policy may be revised
          by EGD to reflect changes in its services related to User Data
          processing and to ensure compliance with relevant laws and
          regulations.
        </p>
        <p>
          In the event of any updates to this User Data Protection Policy, EGD
          will notify Users through its official communication channels. EGD is
          committed to ensuring that Users feel secure and are consistently
          informed about the measures taken to protect their privacy.
        </p>
        <p>
          Furthermore, should any section of this User Data Protection Policy be
          deemed invalid, the validity and enforceability of the remaining
          provisions will remain unaffected.
        </p>
      </Term>

      <Term title="8. EMOS Care" id="data-8">
        <p>
          EGD is prepared to support and address any inquiries Users may have
          concern of this User Data Protection Policy. For assistance, please
          reach out to our customer service team through EMOS Care by emailing
          your questions to <a href="mailto:support@emos.id">support@emos.id</a>
          .
        </p>
      </Term>

      <Term title="9. Policy Update" id="data-9">
        <p>
          The EMOS User Data Protection Policy is effective as of 01 November
          2024.
        </p>
      </Term>
    </section>
  );
};

export default UserDataProtection;
